import { databases } from "@/lib/appwrite";
import { Query } from "appwrite";
import {
    type BaseTravelDocument,
    type BaseFetchTravelsResult,
} from "@/lib/collections/requestsTravels/types";

const DATABASE_ID = import.meta.env.VITE_APPWRITE_DATABASE_ID;
const TRAVELS_COLLECTION_ID = import.meta.env.VITE_TRAVELS_REQUESTS_COLLECTION_ID;

export type TravelDocument = BaseTravelDocument;
export type FetchTravelsResult = BaseFetchTravelsResult;

export class TravelRequestService {
    /**
     * Obtiene los viajes de un usuario (creados por él o donde está como participante)
     * @param userId - ID del usuario actual
     * @param monthFilter - Formato: "YYYY-MM" o undefined para todos
     * @param limit - Cantidad de documentos por página
     * @param offset - Desde qué documento empezar
     * @param statusFilter - "all" | "pending" | "approved" | "rejected"
     */
    static async getTravelsByUser(
        userId: string,
        monthFilter?: string,
        limit: number = 10,
        offset: number = 0,
        statusFilter: string = "all"
    ): Promise<FetchTravelsResult> {
        try {
            // const queries: string[] = [];

            // Filtro de usuario: viajes creados por el usuario O donde está como participante
            // Nota: Appwrite no permite OR directo, así que haremos 2 queries y las combinaremos

            //  Viajes donde el usuario es creador
            const creatorQueries = [
                Query.equal("userId", userId),
                Query.orderDesc("$createdAt"),
                Query.limit(100) // Límite temporal para combinar resultados
            ];

            // Viajes donde el usuario está en usersId[]
            const participantQueries = [
                Query.contains("usersId", userId),

                Query.orderDesc("$createdAt"),
                Query.limit(100)
            ];

            // Ejecutar ambas queries
            const [creatorResult, participantResult] = await Promise.all([
                databases.listDocuments(DATABASE_ID, TRAVELS_COLLECTION_ID, creatorQueries),
                databases.listDocuments(DATABASE_ID, TRAVELS_COLLECTION_ID, participantQueries)
            ]);

            // Combinar resultados y eliminar duplicados
            const allDocs = [...creatorResult.documents, ...participantResult.documents];
            const uniqueDocs = Array.from(
                new Map(allDocs.map(doc => [doc.$id, doc])).values()
            ) as unknown as TravelDocument[];

            // Aplicar filtros manualmente
            let filteredDocs = uniqueDocs;

            // Filtro por mes
            if (monthFilter && monthFilter !== "all") {
                filteredDocs = filteredDocs.filter(doc => {
                    const docDate = new Date(doc.startDate);
                    const docMonth = `${docDate.getFullYear()}-${String(docDate.getMonth() + 1).padStart(2, '0')}`;
                    return docMonth === monthFilter;
                });
            }

            // Filtro por estado
            if (statusFilter && statusFilter !== "all") {
                filteredDocs = filteredDocs.filter(doc => doc.status === statusFilter);
            }

            // Ordenar por fecha de creación descendente
            filteredDocs.sort((a, b) =>
                new Date(b.$createdAt).getTime() - new Date(a.$createdAt).getTime()
            );

            // Aplicar paginación
            const total = filteredDocs.length;
            const paginatedDocs = filteredDocs.slice(offset, offset + limit);

            return {
                documents: paginatedDocs,
                total: total
            };

        } catch (error) {
            console.error("Error fetching travel requests:", error);
            throw new Error("No se pudieron cargar los viajes");
        }
    }


    //Obtiene un viaje especifico por ID

    static async getTravelById(travelId: string): Promise<TravelDocument> {
        try {
            const document = await databases.getDocument(
                DATABASE_ID,
                TRAVELS_COLLECTION_ID,
                travelId
            );
            return document as unknown as TravelDocument;
        } catch (error) {
            console.error("Error fetching travel by ID:", error);
            throw new Error("No se pudo cargar el viaje");
        }
    }


    // Actualiza el estado de un viaje

    static async updateTravelStatus(
        travelId: string,
        status: "pending" | "approved" | "rejected"
    ): Promise<TravelDocument> {
        try {
            const updated = await databases.updateDocument(
                DATABASE_ID,
                TRAVELS_COLLECTION_ID,
                travelId,
                { status }
            );
            return updated as unknown as TravelDocument;
        } catch (error) {
            console.error("Error updating travel status:", error);
            throw new Error("No se pudo actualizar el estado del viaje");
        }
    }
}